import useMedia from '../hooks/use-media';
import type { ResponsiveGridSections, ResponsiveType } from '../types';
import { type Breakpoint, breakpoints } from '../variables';
import { getSizeValue, type Size } from './size';

export const getMediaCss = (
  widthType: string,
  breakpoint: string,
  declaration: string
) => `@media(${widthType}: ${breakpoint}){${declaration}}`;

export const arrayToBreakpoints = (values: string[]) => {
  const smallestBreakpoint = Object.values(breakpoints)[0];

  const maxWithRulesets = getMediaCss(
    'max-width',
    smallestBreakpoint,
    values[0]
  );

  const minWithRulesets = values.reduce((rulesets, value, index) => {
    const breakpoint = `${Object.values(breakpoints)[index]}`;

    return rulesets + getMediaCss('min-width', breakpoint, value);
  }, '');

  return maxWithRulesets + minWithRulesets;
};

export type BreakpointProperties = Breakpoint | 'default';
export type BreakpointObject = Partial<Record<BreakpointProperties, string>>;

export const objectToBreakpoints = (
  property: string,
  obj: BreakpointObject
) => {
  const result = [];

  obj['default'] && result.push(`${property}: ${obj['default']};`);

  delete obj.default;

  Object.entries(obj).forEach(([key, value]) => {
    const breakpoint = key in breakpoints ? breakpoints[key] : key;

    result.push(`@media(min-width: ${breakpoint}){ ${property}: ${value} }`);
  });

  return result.join(' ');
};

export const getResponsiveOneOf = (
  property: string,
  valueArray: ResponsiveType<string>
) => {
  if (Array.isArray(valueArray)) {
    const oneOfValuesArray = valueArray.map(valueArrayItem => {
      return `${property}: ${valueArrayItem};`;
    });

    return arrayToBreakpoints(oneOfValuesArray);
  }

  if (typeof valueArray === 'object') {
    return objectToBreakpoints(property, valueArray);
  }

  return `${property}: ${valueArray};`;
};

export const getAlignmentStyles = (
  prop: string | string[],
  alignments: Record<string, string>
) => {
  if (prop) {
    if (Array.isArray(prop)) {
      const alignItemsArray = [];

      prop.map(propItem => {
        alignItemsArray.push(alignments[propItem]);
      });

      return getResponsiveOneOf('align-items', alignItemsArray);
    }

    return `align-items ${alignments[prop]};`;
  }
};

export const getResponsiveBoolean = (
  property: string,
  valueArray: string[],
  booleanValue: boolean | boolean[]
) => {
  if (Array.isArray(booleanValue)) {
    const booleanValuesArray = booleanValue.map(booleanItem => {
      const value = booleanItem === true ? valueArray[0] : valueArray[1];

      return `${property}: ${value};`;
    });

    return arrayToBreakpoints(booleanValuesArray);
  }

  return `${property}: ${valueArray[0]};`;
};

export const getResponsiveNumber = (
  property: string,
  number: ResponsiveType<number | string>
) => {
  if (number || number === 0) {
    if (Array.isArray(number)) {
      const rulesetsArray = number.map(numberItem => {
        return `${property}: ${numberItem}`;
      });

      return arrayToBreakpoints(rulesetsArray);
    }

    if (typeof number === 'object' && !Array.isArray(number)) {
      return objectToBreakpoints(property, number);
    }

    return `${property}: ${number};`;
  }
};

type SizeParam = Size | Size[];
const transformColumns = (columns: SizeParam) => {
  if (Array.isArray(columns)) {
    return columns.map(column => getSizeValue(column)).join(' ');
  }

  return columns;
};

export const getResponsiveColumns = (
  columns: Record<Breakpoint, SizeParam>
) => {
  if (columns) {
    if (typeof columns === 'object' && !Array.isArray(columns)) {
      const columnsValuesObject: BreakpointObject = {};

      Object.entries(columns).forEach(
        ([key, value]) => (columnsValuesObject[key] = transformColumns(value))
      );

      return objectToBreakpoints('grid-template-columns', columnsValuesObject);
    }

    return `grid-template-columns: ${transformColumns(columns)};`;
  }
};

export const getResponsiveGridSections = (
  gridSections: ResponsiveGridSections,
  sectionType: string
) => {
  if (gridSections) {
    if (typeof gridSections === 'object' && !Array.isArray(gridSections)) {
      const gridSectionsValesObject = {};

      Object.entries(gridSections).forEach(
        ([key, value]) =>
          (gridSectionsValesObject[key] = transformColumns(value))
      );

      return objectToBreakpoints(
        `grid-template-${sectionType}`,
        gridSectionsValesObject
      );
    }

    return `grid-template-${sectionType}: ${transformColumns(gridSections)};`;
  }
};

export const Media = ({ children, queries, values, defaultValue }) => {
  const value = useMedia(queries, values, defaultValue);

  return children(value);
};
